// Bereken per inzicht-cel wat er nodig is om één ISS 2.0-classificatietrede te
// stijgen — de grijze "wat is nog nodig"-hint in de monitor-accordion.
//
// De classificatie is GEEN enkele numerieke ladder: welke poort bindt hangt af
// van de huidige trede (P2-aantallen → B-drempel → P3-replicatie). Zie de
// beslisboom in functions/insightStrength/classifyInsight.js.
//
// De P2-drempels komen bij voorkeur van de SERVER mee (getInsightCells levert
// `p2Targets`). De constanten hieronder zijn nog slechts een terugval voor
// afnemers die ze niet meegeven — ze zijn een spiegel van
// functions/insightStrength/parameters.js en lopen dus uit de pas zodra de
// staffel wijzigt. Dat is precies één keer misgegaan: de UI bleef de afstand
// tot de oude poort tonen na een staffelverlaging.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
// AI-CONTEXT: See docs/features/insight-strength-score-2.md

// De namen zeggen bij WELKE trede een drempel hoort. Dat klinkt pietluttig,
// maar tot 2026-09 heetten deze twee `*_FOR_LEVEL_3` terwijl er de waarden van
// niveau 2 in stonden — de staffelverlaging van 2026.8 veranderde de getallen
// en niet de namen. Een lezer die op de naam afging, las de verkeerde trede.
const P2_MIN_LEVEL = 2 // de poort: niveau 2 (ISS 2026.8), publiceerbaar als indicatief
const P2_ORGS_FOR_GATE = 3 // benchmarkStaffel.orgs[1] — terugval, zie hierboven
const P2_RESPONDENTS_FOR_GATE = 50 // benchmarkStaffel.respondents[1]
const P2_STRONG_MIN_LEVEL = 3 // niveau vereist voor 'sterk onderbouwd' / 'nieuw'
const P2_ORGS_FOR_STRONG = 5 // benchmarkStaffel.orgs[2]
const P2_RESPONDENTS_FOR_STRONG = 100 // benchmarkStaffel.respondents[2]
const INDICATIVE_MIN_B = 0.55 // classification.indicativeMinB
const STRONG_MIN_B = 0.75 // classification.strongMinB

const round2 = (value) => Math.round(value * 100) / 100

/**
 * @typedef {object} InsightNextTierGaps
 * @property {number|null} nOrgs - Extra organisaties nodig om P2 te halen (positief), of null.
 * @property {number|null} nRespondents - Extra respondenten nodig om P2 te halen (positief), of null.
 * @property {number|null} B - Hoeveel de bewijskracht B omhoog moet voor de volgende B-drempel (positief), of null.
 * @property {boolean} awaitingReplication - True als alleen de P3-replicatiepoort de volgende trede nog blokkeert.
 * @property {boolean} awaitingContrast - True als de contrastpoort (ISS 2026.10) de cel onder de trede van de beslisboom houdt.
 */

/**
 * Wat een cel nodig heeft voor de volgende classificatietrede. Reageert op de
 * bindende poort: onder P2 → meer organisaties/respondenten; boven P2 → een
 * hogere B-drempel; met B ≥ 0,75 maar P3 nog niet bevestigd → wachten op
 * replicatie. Een cel op de sterkste trede levert overal null/false.
 *
 * @param {{ gates?: {p2?: {level?: number}, p3?: string}, nOrgs?: number, nRespondents?: number, B?: number }} cell
 * @param {{orgs?: number, respondents?: number, level?: number, strong?: {orgs?: number, respondents?: number, level?: number}}} [targets] - P2-doelen van de server (poort én 'sterk'); zonder dit de terugval hieronder
 * @returns {InsightNextTierGaps}
 */
const getInsightNextTierGaps = (cell = {}, targets = null) => {
    const doelOrgs = (targets && typeof targets.orgs === 'number') ? targets.orgs : P2_ORGS_FOR_GATE
    const doelResp = (targets && typeof targets.respondents === 'number')
        ? targets.respondents
        : P2_RESPONDENTS_FOR_GATE
    const empty = { nOrgs: null, nRespondents: null, B: null, awaitingReplication: false, awaitingContrast: false }

    // ISS 2026.10: de contrastpoort komt ná de beslisboom. Gaf de boom meer dan
    // de cel nu is, dan zijn B en volume al gehaald en blokkeert alleen de
    // vergelijking met organisaties zonder dit patroon. Een hint over B of
    // volume zou dan een stap suggereren die niets oplevert. Server-waarheid:
    // beide classificaties staan op het celdocument, hier wordt niets herrekend.
    if (cell.baseClassification && cell.classification && cell.baseClassification !== cell.classification
        && cell.gates?.p3 !== 'weerlegd') {
        return { ...empty, awaitingContrast: true }
    }

    // Een weerlegd patroon heeft geen volgende trede. Het draaide om in data van
    // ná de ontdekking en is gedegradeerd (ISS 2026.9); meer organisaties of een
    // hogere B tillen het niet terug. Zonder deze tak viel het door naar de
    // replicatieregel hieronder en zette de tabel er "wacht op replicatie" bij —
    // wachten helpt hier niet, en een (+0,13)-hint suggereert een klim naar
    // 'sterk' die niet bestaat.
    if (cell.gates?.p3 === 'weerlegd') return empty

    const p2Level = cell.gates?.p2?.level ?? 0
    const poortNiveau = (targets && typeof targets.level === 'number') ? targets.level : P2_MIN_LEVEL
    const sterkNiveau = (targets && targets.strong && typeof targets.strong.level === 'number') ? targets.strong.level : P2_STRONG_MIN_LEVEL

    // P2 nog niet gehaald → de volgende stap is de P2-poort: genoeg
    // organisaties én respondenten (spreiding heeft geen kolom in deze tabel).
    if (p2Level < poortNiveau) {
        const nOrgs = Number(cell.nOrgs) || 0
        const nRespondents = Number(cell.nRespondents) || 0
        return {
            ...empty,
            nOrgs: nOrgs < doelOrgs ? doelOrgs - nOrgs : null,
            nRespondents: nRespondents < doelResp ? doelResp - nRespondents : null,
        }
    }

    // P2 gehaald → de trede hangt aan B en (bovenaan) aan de P3-poort.
    const b = typeof cell.B === 'number' ? cell.B : 0

    if (b < INDICATIVE_MIN_B) return { ...empty, B: round2(INDICATIVE_MIN_B - b) }
    if (b < STRONG_MIN_B) return { ...empty, B: round2(STRONG_MIN_B - b) }

    // B is sterk genoeg maar het volume zit op de poort en niet op de trede
    // voor 'sterk': dan zijn organisaties/respondenten tot niveau 3 de stap.
    if (p2Level < sterkNiveau) {
        const sterkOrgs = (targets && targets.strong && typeof targets.strong.orgs === 'number') ? targets.strong.orgs : P2_ORGS_FOR_STRONG
        const sterkResp = (targets && targets.strong && typeof targets.strong.respondents === 'number') ? targets.strong.respondents : P2_RESPONDENTS_FOR_STRONG
        const nOrgs = Number(cell.nOrgs) || 0
        const nRespondents = Number(cell.nRespondents) || 0
        return {
            ...empty,
            nOrgs: nOrgs < sterkOrgs ? sterkOrgs - nOrgs : null,
            nRespondents: nRespondents < sterkResp ? sterkResp - nRespondents : null,
        }
    }

    // B is sterk genoeg; alleen replicatie kan nog naar 'sterk onderbouwd' tillen.
    // 'weerlegd' is hierboven al afgevangen, dus dit is echt 'nog niet toetsbaar'.
    if (cell.gates?.p3 !== 'bevestigd') return { ...empty, awaitingReplication: true }

    return empty
}

/**
 * De drempels van de POORT (niveau 2), als terugval voor afnemers die de
 * afstand willen tonen en de servertargets niet bij de hand hebben.
 * Geëxporteerd zodat die getallen niet nóg een keer overgeschreven worden.
 *
 * Geef waar mogelijk de `p2Targets` van de server door in plaats hiervan:
 * deze waarden zijn een kopie en lopen uit de pas zodra de staffel wijzigt.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 */
export const P2_TARGETS = { orgs: P2_ORGS_FOR_GATE, respondents: P2_RESPONDENTS_FOR_GATE }

export default getInsightNextTierGaps
